/**
 * Wave 2.5 — multi-party commercial signer roster.
 * Additive to existing buyerSign / sellerSign flows.
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import {
  useSetPartySignersMutation,
  useSignAsPartySignerMutation,
} from '@/apis/extended/contractRatificationApi';
import type { Contract } from '@/types/ContractType';

type SignerDraft = {
  role: string;
  name: string;
  email: string;
  title: string;
};

type Props = {
  contract: Contract;
  /** Index into partySigners this user may sign, if known */
  canEditRoster?: boolean;
};

const ROLES = [
  'buyer',
  'seller',
  'buyer_entity_officer',
  'seller_entity_officer',
  'guarantor',
  'other',
];

export function PartySignersPanel({ contract, canEditRoster = true }: Props) {
  const [setSigners, { isLoading: saving }] = useSetPartySignersMutation();
  const [signParty, { isLoading: signing }] = useSignAsPartySignerMutation();
  const [rows, setRows] = useState<SignerDraft[]>([]);
  const [signIndex, setSignIndex] = useState<number | null>(null);
  const [signName, setSignName] = useState('');
  const [signData, setSignData] = useState('');

  useEffect(() => {
    const existing = contract.partySigners || [];
    if (existing.length) {
      setRows(
        existing.map((s) => ({
          role: s.role || 'other',
          name: s.name || '',
          email: s.email || '',
          title: s.title || '',
        }))
      );
    } else {
      setRows([
        { role: 'buyer_entity_officer', name: '', email: '', title: 'Manager' },
        { role: 'seller_entity_officer', name: '', email: '', title: 'Seller' },
      ]);
    }
  }, [contract._id, contract.partySigners]);

  const saveRoster = async () => {
    try {
      await setSigners({
        contractId: contract._id,
        signers: rows.map((r, i) => ({ ...r, order: i })),
      }).unwrap();
      toast.success('Signer roster saved');
    } catch {
      toast.error('Could not save signer roster');
    }
  };

  const submitSign = async () => {
    if (signIndex == null || !signData.trim() || !signName.trim()) {
      toast.error('Name and signature text required');
      return;
    }
    try {
      await signParty({
        contractId: contract._id,
        signerIndex: signIndex,
        name: signName.trim(),
        signature: signData.trim(),
      }).unwrap();
      toast.success('Party signature recorded');
      setSignIndex(null);
      setSignName('');
      setSignData('');
    } catch {
      toast.error('Could not record signature');
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Multi-party commercial signers
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Optional entity officers / guarantors. Buyer and seller e-sign flows
          remain unchanged.
        </Typography>

        <Stack gap={1.25}>
          {rows.map((row, idx) => (
            <Stack
              key={idx}
              direction={{ xs: 'column', md: 'row' }}
              gap={1}
              alignItems={{ md: 'center' }}
            >
              <TextField
                select
                size="small"
                label="Role"
                value={row.role}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r, i) =>
                      i === idx ? { ...r, role: e.target.value } : r
                    )
                  )
                }
                sx={{ minWidth: 180 }}
                disabled={!canEditRoster}
              >
                {ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Name"
                value={row.name}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r, i) =>
                      i === idx ? { ...r, name: e.target.value } : r
                    )
                  )
                }
                disabled={!canEditRoster}
              />
              <TextField
                size="small"
                label="Title"
                value={row.title}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r, i) =>
                      i === idx ? { ...r, title: e.target.value } : r
                    )
                  )
                }
                disabled={!canEditRoster}
              />
              <TextField
                size="small"
                label="Email"
                value={row.email}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r, i) =>
                      i === idx ? { ...r, email: e.target.value } : r
                    )
                  )
                }
                disabled={!canEditRoster}
              />
              <Typography variant="caption" color="text.secondary">
                {contract.partySigners?.[idx]?.isStored ? 'Signed' : 'Pending'}
              </Typography>
              <Button size="small" onClick={() => setSignIndex(idx)}>
                Sign
              </Button>
              {canEditRoster && (
                <IconButton
                  size="small"
                  aria-label="Remove signer"
                  onClick={() =>
                    setRows((prev) => prev.filter((_, i) => i !== idx))
                  }
                >
                  ×
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>

        {canEditRoster && (
          <Stack direction="row" gap={1} sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setRows((prev) => [
                  ...prev,
                  { role: 'other', name: '', email: '', title: '' },
                ])
              }
            >
              Add signer
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => void saveRoster()}
              disabled={saving}
            >
              Save roster
            </Button>
          </Stack>
        )}

        {signIndex != null && (
          <Box
            sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider' }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Sign as roster #{signIndex + 1}
            </Typography>
            <Stack gap={1}>
              <TextField
                size="small"
                label="Typed name"
                value={signName}
                onChange={(e) => setSignName(e.target.value)}
              />
              <TextField
                size="small"
                label="Signature (type full legal name)"
                value={signData}
                onChange={(e) => setSignData(e.target.value)}
              />
              <Stack direction="row" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={signing}
                  onClick={() => void submitSign()}
                >
                  Record signature
                </Button>
                <Button size="small" onClick={() => setSignIndex(null)}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
